import { useEffect, useState } from "react";
import { useNavigate, Link, useSearchParams } from "react-router-dom";
import { Gift, X, Lock, RotateCcw, ShieldCheck, Flag, Star } from "lucide-react";
import { toast } from "sonner";
import api, { apiError } from "../lib/api";
import { PricingCards } from "./Landing";
import { useSeo, pricingSchema } from "../lib/useSeo";
import { IntroOfferBanner } from "../components/IntroOfferBanner";
import { useAuth } from "../context/AuthContext";

export default function PricingPage() {
  const navigate = useNavigate();
  const { user, refresh } = useAuth();
  const [params] = useSearchParams();
  const [offer, setOffer] = useState(null);
  const [accepting, setAccepting] = useState(false);
  useSeo({
    title: "Membership Pricing — Try Free for 24 Hours, or from £27/year",
    description: "Try free for 24 hours (no card, 3 sample categories). Join for a month at £4.99, or annually — Student £27, Pro £47, Lifetime £99 one-off.",
    jsonLd: pricingSchema(),
  });
  useEffect(() => { api.post("/events", { type: "pricing_visit" }).catch(() => {}); }, []);

  // Trial-exit discount popup: shows once when eligible (either arriving from
  // the trial-ended email via ?src=trial-ended, or naturally as a trial-used
  // user who has since dropped back to free).
  useEffect(() => {
    if (!user) return;
    if (sessionStorage.getItem("trial_exit_offer_shown") === "1") return;
    const fromEmail = params.get("src") === "trial-ended" || params.get("trial") === "used";
    const eligibleShape = user.trial_used && user.membership === "free";
    if (!fromEmail && !eligibleShape) return;
    api.get("/trial/exit-offer").then(({ data }) => {
      if (data && data.eligible) {
        setOffer(data);
        sessionStorage.setItem("trial_exit_offer_shown", "1");
      }
    }).catch(() => {});
  }, [user, params]);

  const acceptOffer = async () => {
    setAccepting(true);
    try {
      const { data } = await api.post("/trial/exit-offer/accept");
      if (refresh) await refresh();
      toast.success(`${data.percent}% off applied — pick a plan below.`);
      setOffer(null);
    } catch (e) {
      toast.error(apiError(e));
    } finally { setAccepting(false); }
  };
  return (
    <div className="py-16 lg:py-24 px-4 sm:px-6 lg:px-8 bg-[#FAFAF7] min-h-screen">
      <div className="max-w-7xl mx-auto">
        <div className="text-center mb-10">
          <p className="font-display text-[#B8962E] font-bold text-xs sm:text-sm tracking-[0.16em] uppercase mb-3">Universal Short-Command Prompt Dictionary™</p>
          <h1 className="font-display text-3xl sm:text-4xl lg:text-5xl font-extrabold tracking-tight">Membership Pricing</h1>
          <p className="text-slate-500 mt-4 max-w-xl mx-auto text-sm sm:text-base">Instant access on mobile &amp; desktop. Every plan includes the bonus Quick-Command Cheat Sheet.</p>
        </div>

        {/* Card-less 24h trial banner */}
        <Link to="/start-trial" data-testid="pricing-trial-link" className="block max-w-3xl mx-auto mb-10">
          <img src="/assets/trial-24hr-card.webp"
            alt="Try free for 24 hours — no card needed. Full Pro access to Images & Visual Content, Marketing & Business Promotion and Admin & Office Productivity. Start 24-hour pass."
            data-testid="pricing-trial-banner"
            draggable={false}
            className="w-full rounded-2xl shadow-lg hover:scale-[1.02] transition-transform" />
        </Link>

        <div className="mb-12">
          <IntroOfferBanner />
        </div>

        {/* Band 1 — Trust strip (defensible, no fake numbers) */}
        <div data-testid="pricing-trust-strip" className="max-w-4xl mx-auto -mt-6 mb-12">
          <div className="bg-white border border-slate-200 rounded-2xl shadow-sm px-4 sm:px-6 py-4 flex flex-wrap items-center justify-center gap-x-6 gap-y-3 text-xs sm:text-sm font-medium text-slate-700">
            <span className="inline-flex items-center gap-1.5"><Lock size={15} className="text-emerald-600" /> Secured by Stripe</span>
            <span className="hidden sm:inline text-slate-200">•</span>
            <span className="inline-flex items-center gap-1.5"><RotateCcw size={15} className="text-emerald-600" /> Cancel anytime, one click</span>
            <span className="hidden sm:inline text-slate-200">•</span>
            <span className="inline-flex items-center gap-1.5"><ShieldCheck size={15} className="text-emerald-600" /> 7-day no-questions refund</span>
            <span className="hidden sm:inline text-slate-200">•</span>
            <span className="inline-flex items-center gap-1.5"><Flag size={15} className="text-emerald-600" /> UK-based support</span>
          </div>
        </div>

        <PricingCards navigate={navigate} testPrefix="pricing" />
        <p className="text-center text-xs text-slate-400 mt-10">Lifetime members automatically receive everything available in Pro, permanently.</p>

        {/* Band 3 — Testimonials (placeholders ready for real quotes) */}
        <section data-testid="pricing-testimonials" className="mt-14 max-w-5xl mx-auto">
          <div className="text-center mb-6">
            <p className="font-display text-[#B8962E] font-bold text-xs tracking-[0.16em] uppercase">Loved by UK professionals</p>
            <h2 className="font-display text-2xl sm:text-3xl font-extrabold tracking-tight mt-1">What early members are saying</h2>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            {[
              { quote: "Replaced 20 minutes of prompt tweaking with /writeemail. Paid for itself the first week.", name: "Marcus", role: "Agency director", city: "Leeds" },
              { quote: "My team of six now uses the same shorthand. House style stays consistent across every email we send.", name: "Priya", role: "Operations lead", city: "Birmingham" },
              { quote: "The free 24-hour pass sold me. I was back inside an hour to subscribe.", name: "Tom", role: "Freelance consultant", city: "London" },
            ].map((t, i) => (
              <figure key={i} className="bg-white border border-slate-200 rounded-2xl p-6 flex flex-col" data-testid={`pricing-testimonial-${i}`}>
                <div className="flex gap-0.5 text-amber-500 mb-3">
                  {[0,1,2,3,4].map((s) => <Star key={s} size={14} fill="currentColor" />)}
                </div>
                <blockquote className="text-sm text-slate-700 leading-relaxed flex-1">&ldquo;{t.quote}&rdquo;</blockquote>
                <figcaption className="mt-4 pt-4 border-t border-slate-100 text-xs">
                  <p className="font-bold text-slate-900">{t.name}</p>
                  <p className="text-slate-500">{t.role} · {t.city}</p>
                </figcaption>
              </figure>
            ))}
          </div>
          <p className="text-center text-[11px] text-slate-400 mt-4">Early-user bonus: leave a short review in your first week and we&apos;ll extend your subscription by a month.</p>
        </section>

        {/* Teams & Enterprise banner — clickable, routes to /enterprise */}
        <Link to="/enterprise" data-testid="pricing-enterprise-banner-link" className="block mt-14 max-w-3xl mx-auto">
          <img src="/assets/teams-enterprise-banner.webp"
            alt="AI Workforce Enablement — Teams &amp; Enterprise. Enable adoption. Empower staff. Explore now on web.aipromptshortcuts.app/enterprise"
            data-testid="pricing-enterprise-banner"
            className="w-full rounded-2xl shadow-xl hover:scale-[1.02] transition-transform" />
        </Link>
        <p className="text-center text-xs text-slate-400 mt-3">Rolling out to a team? Tap the banner to request an enterprise proposal.</p>
        <Link to="/refer-a-friend" data-testid="pricing-refer-friend-badge-link" className="block">
          <img src="/assets/refer-a-friend.webp" alt="Refer a Friend to XpressPrompts™ — earn free ebooks"
            data-testid="pricing-refer-friend-badge" className="w-full max-w-2xl mx-auto mt-10 drop-shadow-md hover:scale-[1.02] transition-transform" />
        </Link>
        <p className="text-center text-xs text-slate-400 mt-3">Refer a friend and earn a free downloadable ebook every time they join.</p>

        {/* Integrates With Leading AI Apps — social proof via tool coverage */}
        <div data-testid="pricing-integrations-banner" className="mt-14 max-w-4xl mx-auto rounded-2xl overflow-hidden shadow-xl">
          <img src="/assets/integrates-with-ai-apps.png"
            alt="XpressPrompts easily integrates with leading AI apps — ChatGPT, Claude, Google Gemini, Microsoft Copilot, Perplexity, Grok, DeepSeek, Le Chat by Mistral, Meta AI, Poe, You.com, HuggingChat, Kimi, Qwen, Midjourney, Adobe Firefly, Canva Magic Studio, Leonardo AI, Ideogram, Runway, Pika, HeyGen, Synthesia, ElevenLabs, Suno, Notion AI, Jasper, Writesonic"
            className="w-full h-auto block"
            loading="lazy" />
        </div>
        <p className="text-center text-xs text-slate-400 mt-3">Works with the tools your team already uses — no new app to learn.</p>
      </div>

      {offer && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/70 backdrop-blur-sm" data-testid="trial-exit-offer-modal">
          <div className="w-full max-w-md bg-[#0B0F0D] border border-[#D4AF37]/40 rounded-2xl p-7 text-white relative">
            <button data-testid="trial-exit-close-btn" onClick={() => setOffer(null)} aria-label="Close"
              className="absolute top-4 right-4 text-gray-500 hover:text-white transition-colors"><X size={18} /></button>
            <div className="w-12 h-12 rounded-full bg-gradient-to-br from-amber-500 to-yellow-500 flex items-center justify-center">
              <Gift size={22} className="text-slate-950" />
            </div>
            <h2 className="font-display text-2xl font-bold mt-4">Welcome back — here's {offer.percent}% off</h2>
            <p className="text-sm text-gray-300 mt-2">
              Your 24-hour taster is over. As a one-time thank you, here's <span className="text-[#D4AF37] font-bold">{offer.percent}% off</span> your first payment on any annual plan or Lifetime.
            </p>
            <div className="mt-4 bg-white/5 border border-dashed border-[#D4AF37]/60 rounded-xl px-4 py-3 text-center">
              <span data-testid="trial-exit-offer-code" className="font-mono text-lg font-bold tracking-widest text-[#D4AF37] uppercase">{offer.code}</span>
              <p className="text-xs text-gray-400 mt-1">Applies automatically at checkout — nothing to type.</p>
            </div>
            <button data-testid="trial-exit-accept-btn" onClick={acceptOffer} disabled={accepting}
              className="mt-5 w-full bg-gradient-to-r from-amber-500 to-yellow-500 text-slate-950 font-bold py-3 rounded-xl text-sm hover:brightness-105 transition-all disabled:opacity-50">
              {accepting ? "Applying..." : `Apply ${offer.percent}% off & choose a plan`}
            </button>
            <button data-testid="trial-exit-decline-btn" onClick={() => setOffer(null)} disabled={accepting}
              className="mt-3 w-full text-sm font-medium text-gray-400 hover:text-red-400 transition-colors disabled:opacity-50">
              No thanks
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
